/**
 * Hopefield split stage (sections/hf-split-stage.liquid).
 * One state: the colour picked. Tiles, label swatches, container bar, colour rows and weave tabs set it; the stage
 * (tiles, photo, sets, label), the weave tabs, the price rows and the phone photo follow it. Every value comes
 * from the Liquid: a JSON list per colour plus a <template> per colour for the photo and the sets.
 * Without this script the section is complete: tiles are links. Scrolling the colour rows picks each colour in turn.
 */
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const wide = matchMedia('(min-width: 960px)');

class HfSplitStage extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {IntersectionObserver | null} */
  #rowIo = null;
  /** @type {IntersectionObserver | null} */
  #preloadIo = null;
  /** @type {Map<string, {id: string, line: string, name: string, lineName: string, meta: string, available: boolean, left?: number, url: string, shop: string, sets: number}>} */
  #colours = new Map();
  #current = '';
  #queue = Promise.resolve();
  #shuttered = false;

  connectedCallback() {
    const data = this.querySelector('script[data-hf-colours]');
    if (!data) return;
    try {
      for (const colour of JSON.parse(data.textContent || '[]')) this.#colours.set(String(colour.id), colour);
    } catch {
      return;
    }
    if (!this.#colours.size) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#current = this.#colours.has(this.dataset.current || '') ? String(this.dataset.current) : [...this.#colours.keys()][0];

    this.#fillPhoto(this.#current);
    this.#fillSets(this.#current);
    this.#syncTileRoles();
    this.#syncPressed();
    this.#syncRows();
    this.#showLine(this.#colours.get(this.#current)?.line || 'a');

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    wide.addEventListener('change', () => this.#syncTileRoles(), { signal });
    this.#observeScenes();
    this.#observeRows();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#io?.disconnect();
    this.#io = null;
    this.#rowIo?.disconnect();
    this.#rowIo = null;
    this.#preloadIo?.disconnect();
    this.#preloadIo = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[role="tab"][data-hf-line]');
    if (tab instanceof HTMLElement) {
      this.#pickLine(tab.dataset.hfLine || 'a');
      return;
    }

    const picker = event.target.closest('[data-colour]');
    if (!(picker instanceof HTMLElement) || !this.contains(picker)) return;
    if (picker.matches('.hf-tile')) {
      // Tiles are links on phones; on the split layout they pick the colour shown on the stage.
      if (!wide.matches) return;
      event.preventDefault();
    }
    this.select(picker.dataset.colour || '');
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const target = event.target;

    if (target.matches('.hf-tile[role="button"]') && event.key === ' ') {
      event.preventDefault();
      this.select(target.dataset.colour || '');
      return;
    }

    if (target.matches('[role="tab"]') && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const tabs = [...this.querySelectorAll('[role="tab"][data-hf-line]')];
      const index = tabs.indexOf(target);
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;
      const tab = tabs[next];
      if (tab instanceof HTMLElement) {
        tab.focus();
        this.#pickLine(tab.dataset.hfLine || 'a');
      }
    }
  };

  /**
   * Picks a colour and updates everything that shows it.
   * @param {string} id - The starter-set variant id of the colour
   * @param {{ instant?: boolean }} [options] - instant: no tile-swap animation (used while scrolling the colour rows)
   */
  select(id, { instant = false } = {}) {
    const colour = this.#colours.get(id);
    if (!colour || id === this.#current) return;
    this.#current = id;

    this.#queue = this.#queue.then(() => this.#bringToBig(id, instant));
    this.#fillPhoto(id);
    this.#fillSets(id);

    const name = this.querySelector('[data-hf-label-name]');
    const meta = this.querySelector('[data-hf-label-meta]');
    const left = this.querySelector('[data-hf-label-left]');
    const shop = this.querySelector('[data-hf-label-shop]');
    if (name) name.textContent = colour.name;
    if (meta) meta.textContent = colour.meta;
    if (left && typeof colour.left === 'number') left.textContent = String(colour.left);
    if (shop instanceof HTMLAnchorElement) {
      shop.href = colour.url;
      shop.textContent = colour.shop;
    }

    this.#syncPressed();
    this.#showLine(colour.line);
    this.#syncRows();
  }

  /**
   * A weave tab: keep the colour if it is already that weave, else the same colour name in that weave, else the
   * weave's first colour in stock.
   * @param {string} line
   */
  #pickLine(line) {
    const current = this.#colours.get(this.#current);
    if (!current || current.line === line) {
      this.#showLine(line);
      return;
    }
    const inLine = [...this.#colours.values()].filter((colour) => colour.line === line);
    const match =
      inLine.find((colour) => colour.name === current.name) || inLine.find((colour) => colour.available) || inLine[0];
    if (match) this.select(String(match.id));
  }

  /** @param {string} line */
  #showLine(line) {
    for (const tab of this.querySelectorAll('[role="tab"][data-hf-line]')) {
      const on = tab instanceof HTMLElement && tab.dataset.hfLine === line;
      tab.setAttribute('aria-selected', String(on));
      tab.setAttribute('tabindex', on ? '0' : '-1');
    }
    for (const panel of this.querySelectorAll('[role="tabpanel"][data-hf-line]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.toggleAttribute('data-hf-active', panel.dataset.hfLine === line);
    }
    for (const img of this.querySelectorAll('.hf-plate__img, .hf-stage__detail-img')) {
      img.classList.toggle('is-on', img.classList.contains(`hf-plate__img--${line}`) || img.classList.contains(`hf-stage__detail-img--${line}`));
    }
  }

  /**
   * Moves the colour into the big tile; the tile that held it takes the previous colour.
   * @param {string} id
   * @param {boolean} [instant]
   * @returns {Promise<void>}
   */
  #bringToBig(id, instant = false) {
    return new Promise((done) => {
      const big = this.querySelector('.hf-tile--big');
      const holder = this.querySelector(`.hf-tile[data-colour="${CSS.escape(id)}"]`);
      if (!(big instanceof HTMLAnchorElement) || !(holder instanceof HTMLAnchorElement) || big === holder) return done();

      const swap = () => {
        const bigChildren = [...big.childNodes];
        big.replaceChildren(...holder.childNodes);
        holder.replaceChildren(...bigChildren);
        [big.href, holder.href] = [holder.href, big.href];
        [big.dataset.colour, holder.dataset.colour] = [holder.dataset.colour, big.dataset.colour];
        this.#syncPressed();
      };

      if (reduceMotion.matches || instant) {
        swap();
        return done();
      }
      big.classList.add('is-swapping');
      holder.classList.add('is-swapping');
      setTimeout(() => {
        swap();
        requestAnimationFrame(() => {
          big.classList.remove('is-swapping');
          holder.classList.remove('is-swapping');
          done();
        });
      }, 220);
    });
  }

  /** @param {string} id */
  #template(kind, id) {
    const tpl = this.querySelector(`template[data-hf-tpl="${kind}"][data-colour="${CSS.escape(id)}"]`);
    return tpl instanceof HTMLTemplateElement ? tpl : null;
  }

  /** @param {string} id */
  #fillPhoto(id) {
    const tpl = this.#template('photo', id);
    const stage = this.querySelector('[data-hf-photo]');
    if (stage && tpl) {
      let img = [...stage.children].find((child) => child instanceof HTMLElement && child.dataset.colour === id);
      if (!img) {
        const fresh = tpl.content.firstElementChild?.cloneNode(true);
        if (fresh instanceof HTMLElement) {
          fresh.dataset.colour = id;
          stage.append(fresh);
          img = fresh;
        }
      }
      for (const child of stage.children) child.classList.toggle('is-on', child === img);
    }

    // phone: the sticky photograph in the colours block
    const phone = this.querySelector('[data-hf-colour-photo]');
    if (phone && tpl && phone.dataset.colour !== id) {
      const fresh = tpl.content.firstElementChild?.cloneNode(true);
      if (fresh instanceof HTMLElement) {
        fresh.setAttribute('loading', 'eager');
        phone.replaceChildren(fresh);
        phone.dataset.colour = id;
      }
    }
  }

  /** @param {string} id */
  #fillSets(id) {
    const tpl = this.#template('sets', id);
    const sets = this.querySelector('[data-hf-sets]');
    if (sets && tpl) sets.replaceChildren(tpl.content.cloneNode(true));
  }

  #syncTileRoles() {
    for (const tile of this.querySelectorAll('.hf-tile')) {
      if (wide.matches) {
        tile.setAttribute('role', 'button');
      } else {
        tile.removeAttribute('role');
        tile.removeAttribute('aria-pressed');
      }
    }
    this.#syncPressed();
  }

  #syncPressed() {
    for (const el of this.querySelectorAll('[data-colour]')) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.matches('button') || el.getAttribute('role') === 'button') {
        el.setAttribute('aria-pressed', String(el.dataset.colour === this.#current));
      }
    }
  }

  /** Price rows link to the colour picked when their set comes in it. */
  #syncRows() {
    const colour = this.#colours.get(this.#current);
    for (const row of this.querySelectorAll('a[data-hf-row]')) {
      if (!(row instanceof HTMLAnchorElement)) continue;
      if (!row.dataset.hfBase) row.dataset.hfBase = row.getAttribute('href') || '';
      const base = row.dataset.hfBase;
      let href = base;
      if (colour && row.dataset.hfRow === colour.line) {
        for (const pair of (row.dataset.hfVariants || '').split('|')) {
          const at = pair.lastIndexOf('=');
          if (at > 0 && pair.slice(0, at) === colour.name) {
            const url = new URL(base, location.origin);
            url.searchParams.set('variant', pair.slice(at + 1));
            href = url.pathname + url.search;
          }
        }
      }
      row.setAttribute('href', href);
    }
  }

  /** The stage changes scene as the reading column passes. */
  #observeScenes() {
    const stage = this.querySelector('.hf-stage');
    const detail = this.querySelector('[data-hf-detail]');
    if (!(stage instanceof HTMLElement)) return;
    if (!('IntersectionObserver' in window)) {
      detail?.classList.add('is-in');
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
          const scene = entry.target.dataset.hfScene || 'mosaic';
          stage.dataset.scene = scene;
          if (scene !== 'mosaic' && !this.#shuttered) {
            this.#shuttered = true;
            requestAnimationFrame(() => detail?.classList.add('is-in'));
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    for (const section of this.querySelectorAll('.hf-split__read > [data-hf-scene]')) this.#io.observe(section);
  }
  /**
   * Scrolling through the colour rows shows each colour in turn: the row that crosses the middle of the screen
   * becomes the picked colour (stage photo on wide screens, the sticky photo on phones). Photos are loaded just
   * before the rows arrive so the stage never shows a blank frame.
   */
  #observeRows() {
    const rows = [...this.querySelectorAll('.hf-crow[data-colour]')];
    const block = this.querySelector('.hf-colours');
    if (!rows.length || !('IntersectionObserver' in window)) return;

    this.#rowIo = new IntersectionObserver(
      (entries) => {
        const middle = window.innerHeight / 2;
        let best = null;
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
          const box = entry.boundingClientRect;
          const distance = Math.abs(box.top + box.height / 2 - middle);
          if (!best || distance < best.distance) best = { el: entry.target, distance };
        }
        if (best) this.select(best.el.dataset.colour || '', { instant: true });
      },
      // a thin line through the middle of the screen: the row crossing it is the one being read
      { rootMargin: '-49.5% 0px -49.5% 0px', threshold: 0 }
    );
    for (const row of rows) this.#rowIo.observe(row);

    if (block) {
      this.#preloadIo = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#preloadIo?.disconnect();
          this.#preloadIo = null;
          this.#preloadPhotos();
        },
        { rootMargin: '100% 0px 100% 0px', threshold: 0 }
      );
      this.#preloadIo.observe(block);
    }
  }

  /** Loads every colour's photo ahead of the scroll: into the stage (hidden) and into the browser cache for phones. */
  #preloadPhotos() {
    const stage = this.querySelector('[data-hf-photo]');
    for (const tpl of this.querySelectorAll('template[data-hf-tpl="photo"]')) {
      if (!(tpl instanceof HTMLTemplateElement)) continue;
      const id = tpl.dataset.colour || '';
      const source = tpl.content.querySelector('img');
      if (source) {
        const warm = new Image();
        if (source.sizes) warm.sizes = source.sizes;
        if (source.srcset) warm.srcset = source.srcset;
        warm.src = source.src;
      }
      if (!stage || [...stage.children].some((child) => child instanceof HTMLElement && child.dataset.colour === id)) continue;
      const fresh = tpl.content.firstElementChild?.cloneNode(true);
      if (fresh instanceof HTMLElement) {
        fresh.dataset.colour = id;
        if (fresh instanceof HTMLImageElement) fresh.loading = 'eager';
        fresh.querySelectorAll?.('img').forEach((img) => (img.loading = 'eager'));
        stage.append(fresh);
      }
    }
  }
}

if (!customElements.get('hf-split-stage')) customElements.define('hf-split-stage', HfSplitStage);
